import { inject, injectable, injectFromBase } from 'inversify'
import DomainBaseService from '@/services/domain/domainBaseService'
import { DomainSettings } from '@/decorators/domainDecorators'
import { SERVICE_TYPES } from '@/core/container/types'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import type SessionRepository from '@/data/repositories/domain/session/sessionRepository'
import Session from '@/data/models/domain/session/Session'
import { SessionDestination, SessionSource, type ModelRaw } from '@/types'
import type { Translatable } from '@/services/types'
import type SessionShortService from '@/services/domain/session/sessionShortService'
import type SessionShort from '@/data/models/domain/session/SessionShort'
import { TmEntityNotFoundError } from '@/core/error/tmEntityNotFoundError'
import type { SessionArchiveQueryParams } from '@/services/domain/session/types'
import type SmsSessionRecipientsService from '@/services/domain/session/smsSessionRecipientsService'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import type { ModelRelationArray } from '@/services/domain/types'
import type DateTimeService from '@/services/dateTimeService'
import { createAndDownloadFile } from '@/utils/file/createAndDownloadFile'

@DomainSettings({
  model: Session,
})
@injectable()
@injectFromBase()
export default class SessionService extends DomainBaseService<SessionRepository> {
  constructor(
    @inject(SERVICE_TYPES.EntityManager) protected readonly entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) protected readonly subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: Translatable,
    @inject(SERVICE_TYPES.SessionShortService) protected readonly sessionShortService: SessionShortService,
    @inject(SERVICE_TYPES.SmsSessionRecipientsService)
    protected readonly smsSessionRecipientsService: SmsSessionRecipientsService,
    @inject(SERVICE_TYPES.DateTimeService) protected readonly dataTimeService: DateTimeService,
  ) {
    super(entityManager, subscription, preloaderManager)
  }

  public getSessionSourceText(record: Session | SessionShort): string {
    if (record.source === SessionSource.NONE) {
      return this.translateService.t('status.session.none')
    }

    if (record.source === SessionSource.EMAIL) {
      return this.translateService.t('status.session.email')
    }

    if (record.source === SessionSource.MAGICMESSAGE) {
      return this.translateService.t('status.session.e2s')
    }

    if (record.source === SessionSource.TM_ONLINE) {
      return this.translateService.t('status.session.webApp')
    }

    if (record.source === SessionSource.TM_MESSENGER) {
      return this.translateService.t('status.session.messenger')
    }

    if (record.source === SessionSource.TM_TEXTME) {
      return this.translateService.t('status.session.list')
    }

    if (record.source === SessionSource.API) {
      return this.translateService.t('status.session.api')
    }

    if (record.source === SessionSource.TM_WIDGET) {
      return this.translateService.t('status.session.widget')
    }

    if (record.source === SessionSource.TM_MOBILE) {
      return this.translateService.t('status.session.mobile')
    }

    if (record.source === SessionSource.AUTOMATION) {
      return this.translateService.t('status.session.automation')
    }

    if (record.source === SessionSource.IOS_MOBILE) {
      return this.translateService.t('status.session.iosMobile')
    }

    if (record.source === SessionSource.ANDROID_MOBILE) {
      return this.translateService.t('status.session.androidMobile')
    }

    if (record.source === SessionSource.WS_CHAT) {
      return this.translateService.t('status.session.smsChat')
    }

    if (record.source === SessionSource.SURVEY) {
      return this.translateService.t('status.session.survey')
    }

    if (record.source === SessionSource.AUTOMATION_FLOW) {
      return this.translateService.t('status.session.automationFlow')
    }

    if (record.source === SessionSource.DESKTOP) {
      return this.translateService.t('status.session.desktop')
    }

    if (record.source === SessionSource.MCP) {
      return this.translateService.t('status.session.mcp')
    }

    // Should not be returned by the backend, but it was added to support the complete list of statuses.
    if (record.source === SessionSource.RESERVED_0) {
      return this.translateService.t('status.session.reserved')
    }

    throw new TmAbsurdError(record.source)
  }

  public isSms(session: Pick<Session, 'destination'>): boolean {
    return session.destination === SessionDestination.DEST_TEXT_SMS
  }

  public isMms(session: Pick<Session, 'destination'>): boolean {
    return session.destination === SessionDestination.DEST_MMS
  }

  public isVoiceBroadcast(session: Pick<Session, 'destination'>): boolean {
    return session.destination === SessionDestination.DEST_VOICE_BROADCAST
  }

  public isBulkSession(session: ModelRaw<Session> | ModelRaw<SessionShort>) {
    return session.numbersCount > 1
  }

  public isBulkSessionById(sessionId: string) {
    return this.findAnyTypeEntityByIdOrFail(sessionId).numbersCount > 1
  }

  public async archive(id: string) {
    await this.getDomainRepository().archive(id)
    this.notify([id])
  }

  public async unarchive(id: string) {
    await this.getDomainRepository().unarchive(id)
    this.notify([id])
  }

  public async bulkArchive(params: SessionArchiveQueryParams) {
    await this.getDomainRepository().bulkArchive(params)
    this.notify([])
  }

  public async bulkUnarchive(params: SessionArchiveQueryParams) {
    await this.getDomainRepository().bulkUnarchive(params)
    this.notify([])
  }

  public async updateTitle(id: string, title: string) {
    await this.getDomainRepository().updateTitle(id, title)
    await this.updateItemIfExists({ id, title })
  }

  public findAnyTypeEntityById(
    id: string,
    withRelations: ModelRelationArray<SessionShort> = [],
  ): Session | SessionShort | null {
    const session = this.findEntityByIdOrNull(id, withRelations)
    if (session) return session
    return this.findSessionShortByIdOrNull(id, withRelations)
  }

  public findSessionShortByIdOrNull(
    id: string,
    withRelations: ModelRelationArray<SessionShort> = [],
  ): SessionShort | null {
    return this.sessionShortService.findEntityByIdOrNull(id, withRelations)
  }

  public findAnyTypeEntityByIdOrFail(id: string) {
    const session = this.findAnyTypeEntityById(id)
    if (!session) throw new TmEntityNotFoundError(id)
    return session
  }

  public async findOrFillSessionWithRecipients(sessionId: string) {
    const [session, recipients] = await Promise.all([
      this.findOrFill(sessionId),
      this.smsSessionRecipientsService.findOrFill(sessionId, ['contacts', 'groups', 'filteredViews']),
    ])
    return {
      session,
      recipients,
    }
  }

  public canShowDownloadE2sEmailBlock(sessionId: string) {
    const session = this.findEntityByIdOrFail(sessionId)
    return (
      session.emailToSms &&
      session.emailToSms.emailFileDeletedAt !== null &&
      this.dataTimeService.isAfter(
        this.dataTimeService.toDate(session.emailToSms.emailFileDeletedAt),
        this.dataTimeService.now(),
      )
    )
  }

  public async downloadOriginalEmail(sessionId: string) {
    const blob = await this.getDomainRepository().downloadOriginalEmail(sessionId)
    createAndDownloadFile(blob, 'source-email.eml')
  }
}
